import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Upload, FileText, Lock, CheckCircle2, AlertCircle, Loader2, Check, ChevronsUpDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from '@/lib/axios';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

interface Bank {
  code: string;
  name: string;
}

export interface BankStatementUploadRef {
  triggerUpload: () => Promise<boolean>;
}

interface BankStatementUploadProps {
  banks: Bank[];
  onSuccess: (documentId: string) => void;
}

const BankStatementUpload = forwardRef<BankStatementUploadRef, BankStatementUploadProps>(({ banks, onSuccess }, ref) => {
  const [selectedBank, setSelectedBank] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    triggerUpload: async () => {
      return await handleUpload();
    }
  }));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedBank) {
      toast.error('Please select a bank and a file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('bank_code', selectedBank);
    formData.append('password', password);

    try {
      const response = await axios.post('/api/bank/upload-statement/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      if (response.data.status === 'success') {
        toast.success('Statement uploaded successfully!');
        onSuccess(response.data.document_id);
        return false; // Already handled by UI switching to pending state, do not advance yet
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.error || 'Failed to upload statement');
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid gap-6">
        {/* Bank Selection Combobox */}
        <div className="space-y-2">
          <Label className="text-gray-600 font-medium ml-1">Select Your Bank</Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full h-14 rounded-2xl border-gray-200 bg-gray-50/50 hover:bg-gray-100/50 justify-between text-left font-normal"
              >
                {selectedBank
                  ? banks.find((bank) => bank.code === selectedBank)?.name
                  : "Search for your bank..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 max-h-[300px]" align="start">
              <Command>
                <CommandInput placeholder="Search bank..." />
                <CommandEmpty>No bank found.</CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    {banks.map((bank) => (
                      <CommandItem
                        key={bank.code}
                        value={bank.name}
                        onSelect={() => {
                          setSelectedBank(bank.code);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedBank === bank.code ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {bank.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* File Dropzone */}
        <div className="space-y-2">
          <Label className="text-gray-600 font-medium ml-1">Upload Statement (PDF)</Label>
          <div 
            className={`relative group h-40 rounded-3xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center cursor-pointer overflow-hidden
              ${file ? 'border-intellend-green bg-emerald-50/30' : 'border-gray-200 hover:border-intellend-green bg-gray-50/30 hover:bg-emerald-50/10'}`}
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <input 
              id="file-upload"
              type="file" 
              className="hidden" 
              accept=".pdf"
              onChange={handleFileChange}
            />
            
            <AnimatePresence mode="wait">
              {file ? (
                <motion.div 
                  key="file-selected"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex flex-col items-center"
                >
                  <div className="bg-emerald-100 p-3 rounded-2xl text-intellend-green mb-3">
                    <FileText size={28} />
                  </div>
                  <p className="text-sm font-semibold text-gray-800 px-4 text-center line-clamp-1">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{(file.size / (1024 * 1024)).toFixed(2)} MB • PDF</p>
                </motion.div>
              ) : (
                <motion.div 
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center"
                >
                  <div className="bg-gray-100 group-hover:bg-emerald-100 p-3 rounded-2xl text-gray-400 group-hover:text-intellend-green transition-colors mb-3">
                    <Upload size={28} />
                  </div>
                  <p className="text-sm font-medium text-gray-600">Click or drag to upload statement</p>
                  <p className="text-xs text-gray-400 mt-1">Maximum file size: 15MB</p>
                </motion.div>
              )}
            </AnimatePresence>

            {isUploading && (
              <div className="absolute inset-x-0 bottom-0 h-1.5 bg-gray-100">
                <motion.div 
                  className="h-full bg-intellend-green"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Password (Optional) */}
        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <Label className="text-gray-600 font-medium">PDF Password</Label>
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Optional</span>
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input 
              type="password"
              placeholder="Enter password if PDF is protected"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-11 h-14 rounded-2xl border-gray-200 bg-gray-50/50 focus:bg-white transition-all shadow-sm focus:shadow-md"
            />
          </div>
        </div>

        {/* Security Info */}
        <div className="bg-blue-50/50 p-4 rounded-2xl flex gap-3 items-start border border-blue-100/50 mt-4">
          <AlertCircle className="text-blue-500 shrink-0 mt-0.5" size={18} />
          <p className="text-xs text-blue-700 leading-relaxed font-medium">
            Your bank statements are encrypted and used only for credit assessment. We support official PDFs from all major Indian banks.
          </p>
        </div>
      </div>
    </div>
  );
});

BankStatementUpload.displayName = 'BankStatementUpload';

export default BankStatementUpload;
